import { Amplify } from "aws-amplify";
import { fetchAuthSession } from "aws-amplify/auth";
import { ProfileHandling } from "services";
import outputs from "amplify_outputs.json";
import type { Schema } from "schema/data/resource";
import { generateClient } from "aws-amplify/data";

Amplify.configure(outputs);

const client = generateClient<Schema>({
  authMode: "userPool",
});

type Prescreening = Schema["Prescreening"]["type"];

class PrescreeningService {
  private async getAuthInfo() {
    const idToken = (await fetchAuthSession()).tokens?.idToken;
    const token = idToken?.toString();
    const userData = await ProfileHandling.getUserDataFromToken(idToken);
    return { idToken, token, userData };
  }

  async getUserPrescreenings(): Promise<Prescreening[]> {
    const { userData, token } = await this.getAuthInfo();

    const { data, errors } = await client.models.Prescreening.list({
      authMode: "userPool",
      authToken: token,
    });

    const filteredData = data?.filter(
      (prescreening) => prescreening.userId === userData.id,
    );

    if (errors) {
      console.error("Error fetching prescreenings:", errors);
      throw new Error("Failed to fetch prescreenings");
    }

    return filteredData || [];
  }

  async getPrescreeningDetails(id: string): Promise<Prescreening | null> {
    try {
      const { token } = await this.getAuthInfo();

      const { data, errors } = await client.models.Prescreening.get(
        {
          prescreeningId: id,
        },
        {
          authMode: "userPool",
          authToken: token,
        },
      );

      if (errors) {
        console.error(
          `Error fetching prescreening details for ID ${id}:`,
          errors,
        );
        throw new Error("Failed to fetch prescreening details");
      }

      return data;
    } catch (error) {
      console.error(`Error fetching prescreening details for ID ${id}:`, error);
      throw error;
    }
  }

  async deletePrescreenings(ids: string[]): Promise<void> {
    try {
      const { token } = await this.getAuthInfo();

      const deletePromises = ids.map((id) =>
        client.models.Prescreening.delete(
          { prescreeningId: id },
          {
            authMode: "userPool",
            authToken: token,
          },
        ),
      );

      const results = await Promise.all(deletePromises);

      const errors = results
        .filter((result) => result.errors)
        .flatMap((result) => result.errors);
      if (errors.length > 0) {
        console.error("Errors during batch delete:", errors);
        throw new Error("Some prescreenings failed to delete");
      }
    } catch (error) {
      console.error("Error deleting prescreenings:", error);
      throw error;
    }
  }

  async submitPrescreening(input: any): Promise<any> {
    try {
      const { token } = await this.getAuthInfo();

      const data = await client.mutations.submitPrescreening(
        {
          input,
        },
        {
          authMode: "userPool",
          authToken: token,
        },
      );

      return data;
    } catch (error) {
      console.error("Error submitting prescreening:", error);
      throw error;
    }
  }

  extractPrescreeningId(response: any): string {
    try {
      const responseBody = response.data || response;

      const dataRegex = /data":\s*"(.*?)"\s*,\s*"status_code"/;
      const dataMatch = responseBody.match(dataRegex);

      if (!dataMatch || !dataMatch[1]) {
        throw new Error("Failed to find data in response.");
      }

      const dataString = dataMatch[1].replace(/\\/g, "");
      const idRegex = /"prescreeningId":"([^"]+)"/;
      const idMatch = dataString.match(idRegex);

      if (!idMatch || !idMatch[1]) {
        throw new Error("Failed to extract ID from response.");
      }

      return idMatch[1];
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      throw new Error("Failed to extract prescreening ID: " + errorMessage);
    }
  }
}

export const prescreeningService = new PrescreeningService();
